// ShelfWidget.js
// One `kind: 'folder'` manifest entry rendered as a shelf of its files. A folder
// can hold dozens of artifacts (one workbook per broker, say), so the card is a
// compact list and any single file opens full size in a dialog on click. The run
// result's tab strip renders this same shelf for a folder tab, handing in its
// bytes cache so a file opened again is not downloaded again.

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FileViewer from '../../common/FileViewer';
import { entryMeta } from '../../../utils/widgetModel';
import { getSignedUrl } from '../../../services/api_utils';
import { downloadBlob } from '../../../utils/canvasWorkbook';

// The rows of a folder entry are plain file descriptors rather than manifest
// entries, so they are given the `file` kind to reuse the shared meta line.
const fileMeta = (file) => entryMeta({ kind: 'file', mime: file?.mime, size_bytes: file?.size_bytes });

function ShelfWidget({ entry, bytesCache = null }) {
  const files = Array.isArray(entry?.files) ? entry.files : [];
  const [openFile, setOpenFile] = useState(null);
  const [downloadingPath, setDownloadingPath] = useState(null);
  const [error, setError] = useState(null);

  const handleDownload = async (file) => {
    if (!file?.gcs_path) return;
    try {
      setDownloadingPath(file.gcs_path);
      setError(null);
      const signedUrl = await getSignedUrl(file.gcs_path);
      let response = await fetch(signedUrl);
      // A signed URL can go stale between signing and fetching; re-sign and
      // retry once before treating it as a genuine failure.
      if (response.status === 401 || response.status === 403) {
        const freshUrl = await getSignedUrl(file.gcs_path);
        response = await fetch(freshUrl);
      }
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }
      const blob = await response.blob();
      downloadBlob(blob, file.filename || 'download');
    } catch (err) {
      console.error('Failed to download folder file:', err);
      setError(err.message || 'Download failed.');
    } finally {
      setDownloadingPath(null);
    }
  };

  if (files.length === 0) {
    return <Alert severity="info">This folder is empty.</Alert>;
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.5 }}>
          {error}
        </Typography>
      )}
      <Stack spacing={0.5}>
        {files.map((file, index) => (
          <Box
            key={`${file?.gcs_path || file?.filename || 'file'}-${index}`}
            onClick={() => setOpenFile(file)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 0.75,
              minWidth: 0,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <InsertDriveFileIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap>{file?.filename || 'File'}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {fileMeta(file)}
              </Typography>
            </Box>
            <Tooltip title="Download">
              <span>
                <IconButton
                  size="small"
                  disabled={downloadingPath === file?.gcs_path}
                  onClick={(event) => {
                    // The row itself opens the viewer, so the download button
                    // must not also trigger it.
                    event.stopPropagation();
                    handleDownload(file);
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ))}
      </Stack>

      <Dialog open={Boolean(openFile)} onClose={() => setOpenFile(null)} maxWidth="lg" fullWidth>
        {/* `component="div"` because the title row holds buttons and its own
            Typography, which are not valid inside the default heading element. */}
        <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1.5 }}>
          <Typography variant="subtitle1" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {openFile?.filename || 'File'}
          </Typography>
          <Tooltip title="Download">
            <span>
              <IconButton
                size="small"
                disabled={downloadingPath === openFile?.gcs_path}
                onClick={() => handleDownload(openFile)}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={() => setOpenFile(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {/* FileViewer fills its container with `flex: 1`, so the dialog's
              content box is a flex column of a fixed height. */}
          <Box sx={{ height: '70vh', display: 'flex', flexDirection: 'column' }}>
            {openFile && (
              <FileViewer
                file={{
                  gcsPath: openFile.gcs_path,
                  filename: openFile.filename,
                  contentType: openFile.mime,
                }}
                bytesCache={bytesCache}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default ShelfWidget;
